import { useEffect } from "react";

type UseSeoOptions = {
  title: string;
  description: string;
  canonicalPath?: string;
  robots?: "index,follow" | "noindex,follow";
  ogType?: "website" | "article";
};

const SITE_NAME = "In Bitcoin Terms";
const FALLBACK_SITE_URL = "https://inbtcterms.com";
const FALLBACK_OG_IMAGE_PATH = "/bitcoin.png";

const getSiteUrl = () => {
  const configuredUrl = process.env.REACT_APP_SITE_URL?.trim();
  const siteUrl = configuredUrl || FALLBACK_SITE_URL;
  return siteUrl.replace(/\/+$/, "");
};

const normalizePath = (path: string) => {
  if (!path) {
    return "/";
  }

  return path.startsWith("/") ? path : `/${path}`;
};

const upsertMetaTag = (
  attribute: "name" | "property",
  key: string,
  content: string,
) => {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertCanonicalLink = (href: string) => {
  let link = document.head.querySelector(
    "link[rel='canonical']",
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const getPageTitle = (title: string) => {
  const trimmed = title.trim();
  if (!trimmed || trimmed === SITE_NAME) {
    return SITE_NAME;
  }

  return `${trimmed} | ${SITE_NAME}`;
};

export const useSeo = ({
  title,
  description,
  canonicalPath,
  robots = "index,follow",
  ogType = "website",
}: UseSeoOptions) => {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    const currentPath = window.location.pathname || "/";
    const normalizedCanonicalPath = normalizePath(canonicalPath ?? currentPath);
    const canonicalUrl = `${siteUrl}${normalizedCanonicalPath}`;
    const ogImageUrl = `${siteUrl}${FALLBACK_OG_IMAGE_PATH}`;
    const finalTitle = getPageTitle(title);

    document.title = finalTitle;
    upsertCanonicalLink(canonicalUrl);

    upsertMetaTag("name", "description", description);
    upsertMetaTag("name", "robots", robots);

    upsertMetaTag("property", "og:title", finalTitle);
    upsertMetaTag("property", "og:description", description);
    upsertMetaTag("property", "og:type", ogType);
    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag("property", "og:site_name", SITE_NAME);
    upsertMetaTag("property", "og:image", ogImageUrl);

    upsertMetaTag("name", "twitter:card", "summary_large_image");
    upsertMetaTag("name", "twitter:title", finalTitle);
    upsertMetaTag("name", "twitter:description", description);
    upsertMetaTag("name", "twitter:image", ogImageUrl);
  }, [canonicalPath, description, ogType, robots, title]);
};
